// externals
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cloneDeep } from 'lodash';

// validaiton config
import { accessoryValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/accessoryValidationConfig';

// interfaces/types
import type { Accessory } from '@makemydeal/dr-dash-types';
import { useFormValidation } from '@makemydeal/dr-dash-components';
import type { ValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationEngine/types';
import type { AccessoryFieldError } from './manualAccessories/manualAccessoriesTypes';

const VALIDATION_ROOT = 'accessories';
const ACCESSORIES_FORM_VALIDATION_CONFIG: ValidationConfig = {
    [VALIDATION_ROOT]: {
        $collection: accessoryValidationConfig
    }
};

type AccessoryRowErrors = {
    [fieldName in keyof Accessory]?: AccessoryFieldError;
};

type AccessoriesFormErrors = {
    [index: number]: AccessoryRowErrors;
};

const normalizeAccessories = (list: Accessory[]) =>
    list.map((accessory, index) => ({ ...accessory, key: (accessory.key as string | undefined) ?? `${accessory.name}${index}` }));

const getAccessoryRowPath = (index: number) => `${VALIDATION_ROOT}[${index}]`;
const getAccessoryFieldPath = (index: number, fieldName: string) => `${getAccessoryRowPath(index)}.${fieldName}`;

export const hasAnyError = (errors?: AccessoriesFormErrors): boolean => {
    return errors
        ? Object.values(errors).some((rowErrors) => Object.values(rowErrors).some((fieldError) => fieldError?.hasError))
        : false;
};

export const hasAnyFieldError = (errors?: AccessoryRowErrors): boolean => {
    return errors ? Object.values(errors).some((error) => error?.hasError) : false;
};

export const useAccessoryActions = (
    initialAccessories: Accessory[],
    hasLocalModificationsRef?: React.MutableRefObject<boolean>
) => {
    const [accessories, setAccessories] = useState(() => normalizeAccessories(cloneDeep(initialAccessories)));
    const [formErrors, setFormErrors] = useState<AccessoriesFormErrors>({});
    const [hasChanges, setHasChanges] = useState(false);
    // Errors in the whole from that might not been show to the user due to
    // untouched inputs
    const [hasHiddenErrors, setHasHiddenErrors] = useState(false);

    // #region Validation
    const { engine: validator } = useFormValidation({
        initialConfig: ACCESSORIES_FORM_VALIDATION_CONFIG,
        initialValues: {
            // Validation engine gives a different error shape for empty arrays. Undefined makes sure we stay consistent.
            accessories: accessories.length > 0 ? accessories : undefined
        }
    });

    // Careful not to make the validate function unstable,
    // as it will cause validation to be run on unpredictable times
    const validate = useCallback(
        (path: string | string[] = [VALIDATION_ROOT]) => {
            const normalizedPath = Array.isArray(path) ? path : [path];
            const result = validator.getFormErrorState({ flow: validator.ctx.flow, includePaths: normalizedPath });
            return result.accessories;
        },
        [validator]
    );

    const validateField = useCallback(
        (index: number, fieldName: string) => {
            const result = validate(getAccessoryFieldPath(index, fieldName));
            setFormErrors((prev) => ({
                ...prev,
                [index]: {
                    ...prev[index],
                    [fieldName]: result[index][fieldName]
                }
            }));
        },
        [validate]
    );

    const validateRow = useCallback(
        (index: number) => {
            const result = validate(getAccessoryRowPath(index));
            setFormErrors((prev) => ({
                ...prev,
                [index]: result[index]
            }));
        },
        [validate]
    );

    const getFieldValidation = useCallback(
        (index: number, fieldName: string): AccessoryFieldError => {
            return formErrors[index]?.[fieldName] ?? { hasError: false };
        },
        [formErrors]
    );

    const isRowValid = useCallback(
        (index: number): boolean => {
            const rowErrors = formErrors[index];
            return !hasAnyFieldError(rowErrors);
        },
        [formErrors]
    );

    const hasErrors = useMemo(() => {
        return hasAnyError(formErrors);
    }, [formErrors]);

    const imperativeValidate = useCallback(() => {
        const result = validate();
        setFormErrors(result);
    }, [validate]);

    const initialDataValidate = useRef(false);
    /**
     * Perform validation of the inital data when the hook is mounted.
     * This provides validation of the accessories that are already saved, useful when
     * the validation flow changes, for example when the flow changes from default to default+save
     * new rules start to be enforced and we want to make sure that the existing data is validated against those rules,
     * providing feedback to the user about existing issues with the data, instead of just validating new changes going forward.
     */
    useEffect(() => {
        // We want to make sure this validation happens only once at mount,
        // as it is meant to validate the initial data, not to be validating on every render.
        // For now the dependencies of this hook are stable, but the ref is a safeguard in case something changes in the future
        // that causes this effect to be triggered more than once.
        if (!initialDataValidate.current) {
            imperativeValidate();
            initialDataValidate.current = true;
        }
    }, [imperativeValidate]);
    // #endregion Validation

    const setAccessoriesNormalized = useCallback(
        (next: Accessory[] | ((prev: Accessory[]) => Accessory[]), onValidateStateUpdated?: () => void) => {
            if (typeof next === 'function') {
                setAccessories((prev) => {
                    const newValue = normalizeAccessories(next(prev));
                    validator.setValue(VALIDATION_ROOT, newValue, { clearErrors: true });
                    setHasHiddenErrors(hasAnyError(validate()));
                    onValidateStateUpdated?.();
                    return newValue;
                });
            } else {
                const newValue = normalizeAccessories(next);
                validator.setValue(VALIDATION_ROOT, newValue, { clearErrors: true });
                onValidateStateUpdated?.();
                setAccessories(newValue);
                setHasHiddenErrors(hasAnyError(validate()));
            }
        },
        [validator]
    );

    useEffect(() => {
        if (hasLocalModificationsRef?.current) {
            hasLocalModificationsRef.current = false;
            return;
        }
        setAccessoriesNormalized(cloneDeep(initialAccessories));
    }, [initialAccessories]);

    const updateAccessory = useCallback(
        (index: number, fieldName: string, value: string | number | boolean, shouldValidateRow: boolean = false) => {
            setAccessoriesNormalized(
                (prev) => {
                    const updatedAccessories = normalizeAccessories([...prev]);
                    updatedAccessories[index] = {
                        ...updatedAccessories[index],
                        [fieldName]: value
                    };
                    setHasChanges(true);
                    return updatedAccessories;
                },
                () => {
                    if (shouldValidateRow) {
                        validateRow(index);
                    } else {
                        validateField(index, fieldName);
                    }
                }
            );
        },
        [setAccessoriesNormalized, validateField, validateRow]
    );

    const updateAccessoryRow = useCallback(
        (index: number, updatedAccessory: Accessory) => {
            setAccessoriesNormalized(
                (prev) => {
                    const newAccessories = [...prev];
                    newAccessories[index] = updatedAccessory;
                    const updatedAccessories = normalizeAccessories(newAccessories);
                    setHasChanges(true);
                    return updatedAccessories;
                },
                () => {
                    validateRow(index);
                }
            );
        },
        [validateRow]
    );

    const addAccessory = useCallback(
        (newAccessory: Accessory) => {
            setAccessoriesNormalized((prev) => [...prev, newAccessory]);
            setHasChanges(true);
        },
        [setAccessoriesNormalized]
    );

    return {
        accessories,
        addAccessory,
        formErrors,
        getFieldValidation,
        hasChanges,
        hasErrors,
        hasHiddenErrors,
        setAccessories: setAccessoriesNormalized,
        setHasChanges,
        isRowValid,
        updateAccessory,
        updateAccessoryRow,
        imperativeValidate,
        validateField
    };
};
